import SDKConfiguraitonModel from "../Configurator/Models/SDKConfiguraitonModel";
import {
  initDisableCache,
  isDisabledCache,
  setBaseUrl,
} from "../Global/Helper";
import ClassicQuizParticipationBodyModel from "../Namespaces/MiniGames/Models/ClassicQuiz/ClassicQuizParticipationBodyModel";
import MiniGamesFilters from "../Namespaces/MiniGames/Models/Filters/MiniGamesFilters";
import ClassicQuizRemapper from "../Namespaces/MiniGames/Models/Remappers/ClassicQuizRemapper";
import Https from "./Https";
import EitherOrRemapper from "../Namespaces/MiniGames/Models/Remappers/EitherOrRemapper";
import EitherOrParticipationRequestFullModel from "../Namespaces/MiniGames/Models/EitherOr/Participation/EitherOrParticipationRequestFullModel";
import ClassicQuizParticipationsFilters from "../Namespaces/MiniGames/Models/ClassicQuiz/ClassicQuizParticipationsFilters";
import ClassicQuizLeaderboardFilters from "../Namespaces/MiniGames/Models/ClassicQuiz/Rank/ClassicQuizLeaderboardFilters";
import ClassicQuizRankFilters from "../Namespaces/MiniGames/Models/ClassicQuiz/Rank/ClassicQuizRankFilters";
import PersonalityQuizFilters from "../Namespaces/MiniGames/Models/PersonalityQuiz/PersonalityQuizFilters";
import PersonalityQuizRemapper from "../Namespaces/MiniGames/Models/Remappers/PersonalityQuizRemapper";
import PersonalityQuizParticipationBodyModel from "../Namespaces/MiniGames/Models/PersonalityQuiz/Participation/PersonalityQuizParticipationBodyModel";
import PersonalityQuizParticipationsFilters from "../Namespaces/MiniGames/Models/PersonalityQuiz/Participation/PersonalityQuizParticipationsFilters";

export default class MiniGamesHttps extends Https {
  private classicQuizRemapper: ClassicQuizRemapper = null;
  private eitherOrRemapper: EitherOrRemapper = null;
  private personalityQuizRemapper: PersonalityQuizRemapper = null;

  constructor(config: SDKConfiguraitonModel) {
    super(config, setBaseUrl(config.environment, "miniGames"));
    this.classicQuizRemapper = new ClassicQuizRemapper();
    this.eitherOrRemapper = new EitherOrRemapper();
    this.personalityQuizRemapper = new PersonalityQuizRemapper();
  }

  public getClassicQuizzes = async (
    filters?: MiniGamesFilters,
    disableCache?: boolean
  ) => {
    let url = `/classic-quizzes${this.apiSignInUrl}`;

    if (filters) {
      url += filters.constructMainFiltersUrl();
    }

    url += isDisabledCache(disableCache) ? initDisableCache() : "";
    const warnMessage = "There was a problem with fetching classic quizzes";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.classicQuizRemapper.remapClassicQuizzes(data);
  };

  public getClassicQuizById = async (
    classicQuizId: string,
    includeCorrectOptionIds?: boolean,
    disableCache?: boolean
  ) => {
    let url = `/classic-quizzes/${classicQuizId}${this.apiSignInUrl}`;

    if (includeCorrectOptionIds) {
      url += `&include_correct_options=${includeCorrectOptionIds}`;
    }

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = "There was a problem with fetching classic quiz by ID";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.classicQuizRemapper.remapClassicQuizById(data.data);
  };

  public participateInClassicQuiz = async (
    classicQuizId: string,
    questions: ClassicQuizParticipationBodyModel[],
    includeCorrectOptionIds?: boolean
  ) => {
    const url = `/classic-quizzes/${classicQuizId}/participate${this.apiSignInUrl}`;
    const warnMessage = `There was a problem with participating in the following classic quiz: ${classicQuizId}`;
    const method = "POST";
    const body = {
      questions:
        this.classicQuizRemapper.remapClassicQuizParticipationBody(questions),
      include_correct_options: !!includeCorrectOptionIds,
    };
    const data = await this.fetchWithAuth(url, warnMessage, method, body);

    return this.classicQuizRemapper.remapClassicQuizParticipation(data.data);
  };

  public getMyClassicQuizParticipations = async (
    filters: ClassicQuizParticipationsFilters,
    includePagination: boolean
  ) => {
    let url = `/classic-quizzes/participations${this.apiSignInUrl}`;

    if (filters) {
      url += `${filters.constructMainFiltersUrl()}`;
    }

    const warnMessage = `There was a problem with fetching user's own Classic Quiz participations`;
    const data = await this.fetchWithAuth(url, warnMessage);

    return includePagination
      ? this.classicQuizRemapper.remapUserClassicQuizParticipationsWithPagination(
          data
        )
      : this.classicQuizRemapper.remapUserClassicQuizParticipations(data.data);
  };

  public getLeaderboardForClassicQuizzes = async (
    filters: ClassicQuizLeaderboardFilters,
    disableCache: boolean
  ) => {
    let url = `/classic-quizzes/rank${this.apiSignInUrl}`;

    if (filters) {
      url += `${filters.constructMainFiltersUrl()}`;
    }

    url += isDisabledCache(disableCache) ? initDisableCache() : "";
    const warnMessage = `There was a problem with fetching classic quizzes leaderboard`;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.classicQuizRemapper.remapClassicQuizzesLeaderboard(data);
  };

  public getClassicQuizzesUserRanking = async (
    userId: string,
    filters: ClassicQuizRankFilters,
    disableCache: boolean
  ) => {
    let url = `/classic-quizzes/users/${userId}/rank${this.apiSignInUrl}`;

    if (filters) {
      url += `${filters.constructMainFiltersUrl()}`;
    }

    url += isDisabledCache(disableCache) ? initDisableCache() : "";
    const warnMessage = `There was a problem with fetching user's rankings for classic quizzes`;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.classicQuizRemapper.remapClassicQuizUserRankings(data);
  };

  public getClassicQuizQuestionsReport = async (classicQuizId: string) => {
    let url = `/classic-quizzes/${classicQuizId}/questions-report${this.apiSignInUrl}`;

    const warnMessage = `There was a problem with fetching questions report for quiz: ${classicQuizId}`;
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.classicQuizRemapper.remapClassicQuizQuestionsReport(data.data);
  };

  public getClassicQuizResultsSummary = async (
    classicQuizId: string,
    disableCache: boolean
  ) => {
    let url = `/classic-quizzes/${classicQuizId}/results-summary${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage =
      "There was a problem with fetching classic quiz results summary";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.classicQuizRemapper.remapClassicQuizResultsSummary(data.data);
  };

  public getEitherOrs = async (
    filters?: MiniGamesFilters,
    disableCache?: boolean
  ) => {
    let url = `/either-or${this.apiSignInUrl}`;

    if (filters) {
      url += filters.constructMainFiltersUrl();
    }

    url += isDisabledCache(disableCache) ? initDisableCache() : "";
    const warnMessage = "There was a problem with fetching list of Either/Ors";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.eitherOrRemapper.remapEitherOrs(data);
  };

  public getEitherOrById = async (
    eitherOrId: string,
    disableCache?: boolean
  ) => {
    let url = `/either-or/${eitherOrId}${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage = "There was a problem with fetching Either/Or by ID";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.eitherOrRemapper.remapEitherOrById(data.data);
  };

  public getMyEitherOrStats = async (eitherOrId: string) => {
    const url = `/either-or/${eitherOrId}/own-stats${this.apiSignInUrl}`;
    const warnMessage =
      "There was a problem with fetching Either/Or own statistics";
    const data = await this.fetchWithAuth(url, warnMessage);

    return this.eitherOrRemapper.remapEitherOrOwnStats(data.data);
  };

  public getEitherOrResults = async (
    eitherOrId: string,
    disableCache?: boolean
  ) => {
    const url = `/either-or/${eitherOrId}/results${this.apiSignInUrl}${
      isDisabledCache(disableCache) ? initDisableCache() : ""
    }`;
    const warnMessage = "There was a problem with fetching Either/Or results";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.eitherOrRemapper.remapEitherOrResults(data.data);
  };

  public participateInEitherOr = async (
    eitherOrId: string,
    requestBody: EitherOrParticipationRequestFullModel | null
  ) => {
    const url = `/either-or/${eitherOrId}/participate${this.apiSignInUrl}`;
    const warnMessage = "There was a problem with participating in Either/Or";
    const method = "POST";
    const data = await this.fetchWithAuth(
      url,
      warnMessage,
      method,
      requestBody
    );

    return this.eitherOrRemapper.remapEitherOrParticipation(data.data);
  };

  public getPersonalityQuizzes = async (
    filters?: PersonalityQuizFilters,
    disableCache?: boolean
  ) => {
    let url = `/personality-quizzes${this.apiSignInUrl}`;

    if (filters) {
      url += filters.constructMainFiltersUrl();
    }

    url += isDisabledCache(disableCache) ? initDisableCache() : "";
    const warnMessage = "There was a problem with fetching personality quizzes";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.personalityQuizRemapper.remapPersonalityQuizzes(data);
  };

  public getPersonalityQuizById = async (
    personalityQuizId: string,
    disableCache?: boolean
  ) => {
    let url = `/personality-quizzes/${personalityQuizId}${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage =
      "There was a problem with fetching personality quiz by ID";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.personalityQuizRemapper.remapPersonalityQuizById(data.data);
  };

  public participateInPersonalityQuiz = async (
    personalityQuizId: string,
    questions: PersonalityQuizParticipationBodyModel[]
  ) => {
    const url = `/personality-quizzes/${personalityQuizId}/participate${this.apiSignInUrl}`;
    const warnMessage = `There was a problem with participating in the following personality quiz: ${personalityQuizId}`;
    const method = "POST";
    const body = {
      questions:
        this.personalityQuizRemapper.remapPersonalityQuizParticipationBody(
          questions
        ),
    };
    const data = await this.fetchWithAuth(url, warnMessage, method, body);

    return this.personalityQuizRemapper.remapPersonalityQuizParticipation(
      data.data
    );
  };

  public getPersonalityQuizUserParticipation = async (
    personalityQuizId: string,
    profileId: string,
    disableCache?: boolean
  ) => {
    let url = `/personality-quizzes/${personalityQuizId}/participations/${profileId}${this.apiSignInUrl}`;

    if (isDisabledCache(disableCache)) {
      url += initDisableCache();
    }

    const warnMessage =
      "There was a problem with fetching user personality quiz participation";
    const data = await this.fetchWithoutAuth(url, warnMessage);

    return this.personalityQuizRemapper.remapPersonalityQuizParticipation(
      data.data
    );
  };

  public getMyPersonalityQuizParticipations = async (
    filters: PersonalityQuizParticipationsFilters
  ) => {
    let url = `/personality-quizzes/participations${this.apiSignInUrl}`;

    if (filters) {
      url += `${filters.constructMainFiltersUrl()}`;
    }

    const warnMessage = `There was a problem with fetching user's own Personality Quiz participations`;
    const data = await this.fetchWithAuth(url, warnMessage);

    return this.personalityQuizRemapper.remapUserPersonalityQuizParticipations(
      data
    );
  };
}
